import React, { useRef } from "react";
import { Panel, PanelType, Icon, PrimaryButton, TooltipHost, DirectionalHint } from "@fluentui/react";
import { logEvent } from "../../helper/AppInsightsHelper";

interface ScenarioFeature {
    supportedFeatures: string[];
    unsupportedFeatures: string[];
}

interface ScenarioDetailsPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    scenarioName: string | null;
    scenarioId: number | null;
    scenarioDescription: string | null;
    scenarioDetails: { [key: number]: ScenarioFeature };
    onStartChat?: () => void;
    isSelected?: boolean;
}

export const ScenarioDetailsPanel: React.FC<ScenarioDetailsPanelProps> = ({
    isOpen,
    onDismiss,
    scenarioName,
    scenarioId,
    scenarioDescription,
    scenarioDetails,
    onStartChat,
    isSelected
}) => {
    const descriptionRef = useRef<HTMLParagraphElement>(null);

    const getPanelWidth = () => {
        if (typeof window === "undefined") return "500px";
        const width = window.innerWidth;
        if (width < 768) return "100%";
        if (width < 1200) return "500px";
        return "600px";
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            isLightDismiss={true}
            type={PanelType.custom}
            customWidth={getPanelWidth()}
            headerText={scenarioName ? `${scenarioName} - Details` : "Scenario Details"}
            closeButtonAriaLabel="Close"
            styles={{
                header: {
                    borderBottom: "1px solid #edebe9"
                },
                headerText: {
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#323130"
                },
                content: {
                    paddingBottom: "20px"
                },
                scrollableContent: {
                    height: "100%",
                    overflow: "auto"
                },
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.4)"
                }
            }}
        >
            <div style={{ padding: "16px 4px" }}>
                {/* Scenario Description - Always show if available */}
                {scenarioDescription && scenarioDescription.trim().length > 0 && (
                    <div style={{
                        marginBottom: "24px",
                        padding: "16px",
                        background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)",
                        borderRadius: "8px",
                        border: "1px solid #d1e7fd"
                    }}>
                        <h3 style={{
                            margin: "0 0 12px 0",
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#0078D7"
                        }}>
                            Description
                        </h3>
                        <TooltipHost
                            content={scenarioDescription.replace(/\\n/g, "\n")}
                            calloutProps={{
                                directionalHint: DirectionalHint.topCenter,
                                directionalHintFixed: true,
                                isBeakVisible: true,
                                beakWidth: 12,
                                gapSpace: 6,
                                styles: {
                                    calloutMain: {
                                        background: "#ffffff",
                                        color: "#242424",
                                        borderRadius: "8px",
                                        padding: "12px 16px",
                                        fontSize: "13px",
                                        maxWidth: "700px",
                                        whiteSpace: "pre-wrap",
                                        wordWrap: "break-word",
                                        lineHeight: "1.5",
                                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                                        border: "1px solid #e1e4e8"
                                    }
                                }
                            }}
                        >
                            <p
                                ref={descriptionRef}
                                style={{
                                    margin: 0,
                                    fontSize: "13px",
                                    color: "#242424",
                                    lineHeight: "1.6",
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    textOverflow: "ellipsis",
                                    cursor: "help",
                                    transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = "#0078D7";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = "#242424";
                                }}
                            >
                                {scenarioDescription.replace(/\\n/g, " ")}
                            </p>
                        </TooltipHost>
                    </div>
                )}

                {scenarioId !== null && scenarioDetails[scenarioId] ? (
                    <>
                        {/* Supported Features Section */}
                        <FeatureSection
                            title="Supported Features"
                            items={scenarioDetails[scenarioId].supportedFeatures}
                            iconName="SkypeCircleCheck"
                            color="#107c10"
                            backgroundColor="linear-gradient(135deg, #f0fff0 0%, #e8f5e9 100%)"
                            borderColor="#c8e6c9"
                            hoverColor="linear-gradient(135deg, #e8f5e9 0%, #dcedc8 100%)"
                        />

                        {/* Unsupported Features Section */}
                        <FeatureSection
                            title="Unsupported Features"
                            items={scenarioDetails[scenarioId].unsupportedFeatures}
                            iconName="StatusErrorFull"
                            color="#d13438"
                            backgroundColor="linear-gradient(135deg, #fff5f5 0%, #ffe6e6 100%)"
                            borderColor="#ffcdd2"
                            hoverColor="linear-gradient(135deg, #ffe6e6 0%, #ffcccc 100%)"
                        />

                        {/* Start Chat Button */}
                        {onStartChat && (
                            <div style={{
                                marginTop: "24px",
                                padding: "20px",
                                background: "linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)",
                                borderRadius: "12px",
                                border: "2px solid #e3f2fd"
                            }}>
                                {!isSelected && (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "12px",
                                        padding: "10px",
                                        background: "linear-gradient(135deg, #fff4e6 0%, #ffe8cc 100%)",
                                        borderRadius: "8px",
                                        border: "1px solid #ffd699"
                                    }}>
                                        <Icon iconName="Info" style={{ fontSize: "14px", color: "#c87900", flexShrink: 0 }} />
                                        <span style={{
                                            fontSize: "12px",
                                            color: "#6b4400",
                                            lineHeight: "1.4",
                                            fontWeight: 500
                                        }}>
                                            Please select this scenario from the main view to enable the Start Chat button
                                        </span>
                                    </div>
                                )}
                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    <PrimaryButton
                                        text={isSelected ? "Start Chat with this Scenario" : "Select Scenario First"}
                                        onClick={() => {
                                            if (isSelected) {
                                                // Track Start Chat button click with scenario ID
                                                logEvent("StartChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                                                    scenarioId: scenarioId
                                                });
                                                onStartChat();
                                                onDismiss();
                                            }
                                        }}
                                        iconProps={{ iconName: isSelected ? "Chat" : "Lock" }}
                                        disabled={!isSelected}
                                        styles={{
                                            root: {
                                                borderRadius: "12px",
                                                padding: "16px 32px",
                                                fontSize: "15px",
                                                fontWeight: 600,
                                                background: isSelected
                                                    ? "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)"
                                                    : "linear-gradient(135deg, #e1e1e1 0%, #d1d1d1 100%)",
                                                border: "none",
                                                boxShadow: isSelected
                                                    ? "0 6px 20px rgba(0, 120, 215, 0.35)"
                                                    : "0 2px 8px rgba(0, 0, 0, 0.15)",
                                                transition: "all 0.3s ease",
                                                width: "100%",
                                                maxWidth: "350px",
                                                height: "52px",
                                                cursor: isSelected ? "pointer" : "not-allowed",
                                                ":hover": isSelected ? {
                                                    transform: "translateY(-2px) scale(1.02)",
                                                    boxShadow: "0 8px 24px rgba(0, 120, 215, 0.45)",
                                                    background: "linear-gradient(135deg, #106ebe 0%, #0078D7 100%)"
                                                } : {},
                                                ":active": isSelected ? {
                                                    transform: "translateY(0px) scale(0.98)"
                                                } : {}
                                            },
                                            label: {
                                                color: isSelected ? "white" : "#666",
                                                fontSize: "15px"
                                            },
                                            icon: {
                                                color: isSelected ? "white" : "#666",
                                                fontSize: "16px"
                                            },
                                            flexContainer: {
                                                gap: "10px"
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <NoDetailsMessage />
                )}
            </div>
        </Panel>
    );
};

interface FeatureSectionProps {
    title: string;
    items: string[];
    iconName: string;
    color: string;
    backgroundColor: string;
    borderColor: string;
    hoverColor: string;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
    title,
    items,
    iconName,
    color,
    backgroundColor,
    borderColor,
    hoverColor
}) => {
    return (
        <div style={{ marginBottom: "24px" }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
                paddingBottom: "8px",
                borderBottom: "2px solid #e1e7f5"
            }}>
                <div style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 6px ${color}33`
                }}>
                    <Icon iconName={iconName} style={{ fontSize: "16px", color: "white" }} />
                </div>
                <h3 style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: color
                }}>
                    {title}
                </h3>
            </div>
            <div style={{ paddingLeft: "4px" }}>
                {items && items.length > 0 ? (
                    items.map((item, index) => (
                        <FeatureItem
                            key={index}
                            text={item}
                            color={color}
                            backgroundColor={backgroundColor}
                            borderColor={borderColor}
                            hoverColor={hoverColor}
                        />
                    ))
                ) : (
                    <div style={{
                        padding: "16px"
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: "13px",
                            color: "#6c757d",
                            lineHeight: "1.5"
                        }}>
                            {title === "Supported Features"
                                ? "No supported features information is available for this scenario yet."
                                : "No unsupported features information is available for this scenario yet."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

interface FeatureItemProps {
    text: string;
    color: string;
    backgroundColor: string;
    borderColor: string;
    hoverColor: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
    text,
    color,
    backgroundColor,
    borderColor,
    hoverColor
}) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <div
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                marginBottom: "8px",
                padding: "8px 10px",
                background: isHovered ? hoverColor : backgroundColor,
                borderRadius: "6px",
                border: `1px solid ${borderColor}`,
                transition: "all 0.2s ease",
                transform: isHovered ? "translateX(2px)" : "translateX(0)"
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: `0 1px 4px ${color}4d`
            }}>
                <Icon
                    iconName={color.includes("107c10") ? "CheckMark" : "Cancel"}
                    style={{ fontSize: "10px", color: "white", fontWeight: "bold" }}
                />
            </div>
            <span style={{
                fontSize: "13px",
                color: color.includes("107c10") ? "#1a5a1a" : "#5a1a1a",
                lineHeight: "1.4",
                flex: 1,
                fontWeight: 500
            }}>
                {text}
            </span>
        </div>
    );
};

const NoDetailsMessage: React.FC = () => {
    return (
        <div style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "#616161"
        }}>
            <Icon iconName="Info" style={{ fontSize: "48px", color: "#0078D7", marginBottom: "16px" }} />
            <p style={{ fontSize: "16px", margin: 0 }}>
                Details for this scenario are not available yet.
            </p>
        </div>
    );
};
